<script lang="ts">
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../../services/gen_2_http_reqs';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		get_gen_2_component_channel_structure,
		getSettingUiComponent,
		isNoRelayPlug,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	import type { SettingRequest } from '../../../../../types/requests/settings/setting';
	import {
		Checkbox,
		ColorWheel,
		Container,
		Radio,
		SliderV2,
		Text,
	} from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from '../../../../../helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import SettingGuide from '../../../SettingGuide.svelte';
	import NightSchedule from '../../../NightMode/NightModeComponents/NightSchedule.svelte';
	import NightModeList from '../../../NightMode/NightModeComponents/NightModeList.svelte';
	import { buildPlugUiLedsConfig } from './leds_payload';

	export let device;

	const hasRelay = !isNoRelayPlug(device.type);
	const id = real_id(device.id);
	const settings = readDeviceSettings(id);
	const min = 0;
	const max = 100;
	const modes = ['power', 'switch', 'off'];
	const plugUi = getSettingUiComponent(device);
	const structure =
		device.type === 'S4PL-00416EU' || device.type === 'S4PL-00415US'
			? 'switch:0'
			: get_gen_2_component_channel_structure(device);
	const visualModes = hasRelay ? modes : ['power', 'color', 'off'];

	const leds = settings?.[plugUi]?.leds;
	const nightModeConfig = leds?.night_mode ?? {
		enable: false,
		brightness: 10,
		active_between: ['00:00', '00:00'],
	};
	if (
		(Array.isArray(nightModeConfig.active_between) && nightModeConfig.active_between.length < 1) ||
		!Array.isArray(nightModeConfig.active_between)
	) {
		nightModeConfig.active_between = ['00:00', '00:00'];
	}

	let brightnessValue: number = leds?.colors?.power?.brightness || 0;
	let colorOn: number[] = leds?.colors?.[structure]?.on?.rgb?.map((c) => Math.round(c * 2.55));
	let colorOff: number[] = leds?.colors?.[structure]?.off?.rgb?.map((c) => Math.round(c * 2.55));
	let brightnessOn: number = leds?.colors?.[structure]?.on?.brightness;
	let brightnessOff: number = leds?.colors?.[structure]?.off?.brightness;
	let selectedMode: string = leds?.mode;
	let nightEnabled = nightModeConfig.enable;
	let nightMode = {
		active_between: nightModeConfig.active_between,
		brightness: nightModeConfig.brightness,
	};
	let nightProps = {
		start: nightMode.active_between[0],
		end: nightMode.active_between[1],
		brightness: nightMode.brightness,
	};
	let showNightModal = false;
	let loading = false;

	let cacheSnapshot = JSON.stringify({
		selectedMode,
		brightnessValue,
		brightnessOn,
		brightnessOff,
		colorOn,
		colorOff,
		nightEnabled,
		nightStart: nightProps.start,
		nightEnd: nightProps.end,
		nightBrightness: nightProps.brightness,
	});

	$: snapshot = JSON.stringify({
		selectedMode,
		brightnessValue,
		brightnessOn,
		brightnessOff,
		colorOn,
		colorOff,
		nightEnabled,
		nightStart: nightProps.start,
		nightEnd: nightProps.end,
		nightBrightness: nightProps.brightness,
	});
	$: isDirty = snapshot !== cacheSnapshot;
	$: ledsOff = selectedMode === 'off';
	$: nightUiDisabled = ledsOff;

	const modeCopy = {
		power: {
			title: $format('label_led_mode_power', { default: 'Power consumption' }),
			description: $format('label_led_mode_power_desc', {
				default: 'The LED colour follows how much power the plug is using.',
			}),
		},
		switch: {
			title: $format('label_led_mode_switch', { default: 'On / off' }),
			description: $format('label_led_mode_switch_desc', {
				default: 'Pick colours for when the socket is on and when it is off.',
			}),
		},
		color: {
			title: $format('label_led_mode_color', { default: 'Color' }),
			description: $format('label_led_mode_color_desc', {
				default: 'Pick a colour for the LED.',
			}),
		},
		off: {
			title: $format('label_led_mode_off', { default: 'Off' }),
			description: $format('label_led_mode_off_desc', {
				default: 'Turn the LED ring off.',
			}),
		},
	};

	const a11yProps = (value: number) => ({
		sliderValueText: $format('current_brightness_value', {
			values: { value },
			default: `Current brightness value is ${value}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value },
			default: `Current brightness value is ${value}`,
		}),
	});

	const splitColors = (color) => {
		return { r: color[0], g: color[1], b: color[2] };
	};
	const joinColors = (color) => {
		return [color.r, color.g, color.b];
	};
	const changeColor = (e, color) => {
		const value = joinColors(e.detail.color);
		if (color === 'off') {
			colorOff = value;
		} else {
			colorOn = value;
		}
	};

	const handleNightToggle = () => {
		if (nightUiDisabled) return;
		nightEnabled = !nightEnabled;
	};

	const handleNightSave = (e) => {
		const next = { ...e.detail };
		nightMode.active_between = [next.start, next.end];
		nightMode.brightness = next.brightness;
		nightProps = {
			start: typeof next.start !== 'object' ? next.start : '00:00',
			end: typeof next.end !== 'object' ? next.end : '00:00',
			brightness: next.brightness,
		};
	};

	const sentRequest = async () => {
		if (!isDirty) return;
		loading = true;
		const config = buildPlugUiLedsConfig({
			selectedMode,
			structure,
			hasRelay,
			powerBrightness: brightnessValue,
			colorOn,
			brightnessOn,
			colorOff,
			brightnessOff,
			nightEnable: nightEnabled,
			nightStart: nightProps.start,
			nightEnd: nightProps.end,
			nightBrightness: nightProps.brightness,
		});
		const res: SettingRequest = await gen2Request(device, `${plugUi}.SetConfig`, {
			id: device.channel,
			config,
		});
		onSettingsResponse(res, device);
		if (res.isok) {
			cacheSnapshot = snapshot;
		}
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: sentRequest,
			loading,
			disabled: loading || !isDirty,
			icon: 'fas fa-save',
		});
	};
</script>

<SettingGuide
	text={$format('settings_guide_led_indicators_plug', {
		default:
			'The LED ring can follow power use, show on/off colours, or stay off. Night mode can dim it on a schedule.',
	})}
/>

<Container padding="s" type="borderSolid" backgroundColor="color-base">
	<Text styleType="h2" class="mb-s">
		{capFirstChar($format('settings_heading_led_mode', { default: 'LED mode' }))}
	</Text>
	{#each visualModes as mode (mode)}
		<Radio
			size="m"
			class="mb-s"
			bind:selected={selectedMode}
			value={modes[visualModes.indexOf(mode)]}
			id={`radio-led-mode-${mode}`}
			info={modeCopy[mode]?.description}
		>
			{modeCopy[mode]?.title}
		</Radio>
	{/each}

	{#if selectedMode === 'power' && hasRelay}
		<SliderV2
			id="slider-led-power-brightness"
			a11yProps={a11yProps(brightnessValue)}
			class="mb-s"
			title={capFirstChar($format('collapse_title_brightness'))}
			type="horizontal"
			bind:value={brightnessValue}
			{min}
			{max}
		/>
	{:else if selectedMode === 'switch'}
		<Text styleType="body-small" class="mb-s"
			>{$format('title_led_ind_color_on', {
				default: 'Color when Plug is On',
			})}</Text
		>
		{#if colorOn}
			<ColorWheel
				id="colorwheel-led-on"
				hideSaveBtn
				color={splitColors(colorOn)}
				on:color_change={(e) => changeColor(e, 'on')}
			/>
		{/if}
		<SliderV2
			id="slider-led-on-brightness"
			a11yProps={a11yProps(brightnessOn)}
			class="mb-s"
			title={capFirstChar($format('collapse_title_brightness'))}
			titleTypography="body-small"
			type="incremented"
			bind:value={brightnessOn}
			{min}
			{max}
			lineMarkers={[
				{ value: min, showLabel: true },
				{ value: max, showLabel: true },
			]}
		/>
		{#if colorOff && hasRelay}
			<Text styleType="body-small" class="mb-s"
				>{$format('title_led_ind_color_off', {
					default: 'Color when Plug is Off',
				})}</Text
			>
			<ColorWheel
				id="colorwheel-led-off"
				hideSaveBtn
				color={splitColors(colorOff)}
				on:color_change={(e) => changeColor(e, 'off')}
			/>
			<SliderV2
				id="slider-led-off-brightness"
				a11yProps={a11yProps(brightnessOff)}
				class="mb-s"
				title={capFirstChar($format('collapse_title_brightness'))}
				titleTypography="body-small"
				type="incremented"
				bind:value={brightnessOff}
				{min}
				{max}
				lineMarkers={[
					{ value: min, showLabel: true },
					{ value: max, showLabel: true },
				]}
			/>
		{/if}
	{/if}
</Container>

<Container padding="s" type="borderSolid" backgroundColor="color-base" class="mt-m">
	<SettingGuide
		title={$format('settings_heading_led_night_mode', { default: 'Night mode' })}
		text={$format('settings_guide_led_night_mode', {
			default: 'Dim the LED during a time window. Has no effect while the LED is off.',
		})}
	/>
	<Checkbox
		class="mb-s"
		size="m"
		checked={nightEnabled}
		disabled={nightUiDisabled}
		on:click={handleNightToggle}
		id="checkbox-led-night-mode"
	>
		{$format('label_enable_night_mode')}
	</Checkbox>
	<Text class="mb-s" element="h3">{$format('active_nigthmode')}</Text>
	{#key `${nightMode.active_between}|${nightEnabled}|${nightUiDisabled}`}
		<NightModeList
			disabled={!nightEnabled || nightUiDisabled}
			{nightMode}
			on:edit={() => {
				if (!nightUiDisabled && nightEnabled) showNightModal = true;
			}}
		/>
	{/key}
</Container>

{#if showNightModal}
	<NightSchedule bind:props={nightProps} bind:showModal={showNightModal} on:save={handleNightSave} />
{/if}
